/**
 * One order: where it stands, what it was paid, what it cost, and the way to move it on.
 *
 * The status buttons offer only the moves the lifecycle allows (`lifecycle.ts`). Cancelling is
 * final, so it asks once more. A refund is its own form, because it is money going back as well
 * as a status: the server records both together.
 */

import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Field, Input, Notice, Select } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { Amount } from "@/features/receipts/ReceiptBits";
import { amountInput } from "@/features/receipts/schemas";
import { formatCalendarDate, todayIso } from "@/lib/dates";
import { fieldMessage } from "@/lib/fieldMessage";
import { DueDate, OrderStatusBadge } from "./OrderBits";
import {
  canRemovePayment,
  isEditable,
  nextStatuses,
  takesPayments,
  type OrderStatus,
} from "./lifecycle";
import {
  orderMoneyQuery,
  orderPaymentsQuery,
  orderPurchasesQuery,
  orderQuery,
  paymentMethodsQuery,
  useChangeStatus,
  useDeletePayment,
  useRecordPayment,
  useRefund,
  useUpdateOrder,
  type Order,
  type OrderMoney,
} from "./queries";
import {
  orderDetailsSchema,
  paymentSchema,
  toOrderPatch,
  toPaymentBody,
  toRefundBody,
  type OrderDetailsValues,
  type PaymentValues,
} from "./schemas";

export function OrderPage() {
  const { t } = useTranslation();
  const { orderId } = useParams({ from: "/app/orders/$orderId" });
  const order = useQuery(orderQuery(orderId));

  return (
    <section className="space-y-5">
      <Link
        to="/orders"
        className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t("orders.title")}
      </Link>
      {order.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {order.isError && <Notice tone="error">{t("orders.errors.loadFailed")}</Notice>}
      {order.data && <OrderBody order={order.data} />}
    </section>
  );
}

function OrderBody({ order }: { order: Order }) {
  const [editing, setEditing] = useState(false);

  return (
    <>
      <header className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-semibold text-stone-900">{order.description}</h1>
          <OrderStatusBadge status={order.status} />
        </div>
        <p className="flex flex-wrap gap-x-2 text-sm text-stone-600">
          <span>{order.customer.name}</span>
          <DueDate date={order.due_date} />
        </p>
      </header>

      <StatusActions order={order} />
      <MoneyPanel orderId={order.id} />

      {editing ? (
        <DetailsForm order={order} onDone={() => setEditing(false)} />
      ) : (
        <Details order={order} onEdit={() => setEditing(true)} />
      )}

      <Payments order={order} />
      <Purchases orderId={order.id} />
    </>
  );
}

// --- status -------------------------------------------------------------------------------------

function StatusActions({ order }: { order: Order }) {
  const { t } = useTranslation();
  const change = useChangeStatus(order.id);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const moves = nextStatuses(order.status);
  if (moves.length === 0) return null;

  async function go(to: OrderStatus) {
    setError(undefined);
    try {
      await change.mutateAsync(to);
      setConfirmingCancel(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("orders.errors.saveFailed"));
    }
  }

  const forward = moves.filter((to) => to !== "cancelled");
  return (
    <div className="space-y-2">
      {forward.map((to) => (
        <Button key={to} type="button" disabled={change.isPending} onClick={() => void go(to)}>
          {t(`orders.move.${to}`)}
        </Button>
      ))}
      {moves.includes("cancelled") &&
        (confirmingCancel ? (
          <div className="space-y-2 rounded-lg bg-red-50 p-3">
            <p className="text-sm text-red-800">{t("orders.cancelExplain")}</p>
            <div className="flex gap-2">
              <Button
                type="button"
                className="bg-red-700 hover:bg-red-800"
                disabled={change.isPending}
                onClick={() => void go("cancelled")}
              >
                {t("orders.cancelConfirm")}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setConfirmingCancel(false)}>
                {t("orders.keep")}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="text-sm text-red-700 underline"
            onClick={() => setConfirmingCancel(true)}
          >
            {t("orders.move.cancelled")}
          </button>
        ))}
      {error && <Notice tone="error">{error}</Notice>}
    </div>
  );
}

// --- money --------------------------------------------------------------------------------------

function MoneyPanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const money = useQuery(orderMoneyQuery(orderId));
  if (!money.data) return null;
  const m: OrderMoney = money.data;
  const row = (label: string, value: OrderMoney["cost"] | null, strong = false) => (
    <div className="flex justify-between gap-3">
      <dt className={strong ? "font-semibold text-stone-900" : "text-stone-600"}>{label}</dt>
      <dd>
        <Amount money={value} className={strong ? "font-semibold text-stone-900" : ""} />
      </dd>
    </div>
  );
  return (
    <dl className="space-y-1.5 rounded-lg border border-stone-200 bg-white px-4 py-3">
      {row(t("orders.money.paid"), m.net_paid)}
      {m.balance && row(t("orders.money.balance"), m.balance, true)}
      {row(t("orders.money.cost"), m.cost)}
      {row(t("orders.money.margin"), m.margin, true)}
    </dl>
  );
}

// --- details ------------------------------------------------------------------------------------

function Details({ order, onEdit }: { order: Order; onEdit: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-stone-800">{t("orders.details")}</h2>
        {isEditable(order.status) && (
          <button
            type="button"
            className="text-brand-700 inline-flex items-center gap-1 text-sm underline"
            onClick={onEdit}
          >
            <Pencil className="h-4 w-4" aria-hidden />
            {t("orders.edit")}
          </button>
        )}
      </div>
      <dl className="space-y-1.5 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-stone-600">{t("orders.quote")}</dt>
          <dd>
            <Amount money={order.quoted_total} />
          </dd>
        </div>
        {order.items.map((item) => (
          <div key={item.id} className="flex justify-between gap-3">
            <dt className="text-stone-700">
              {Number(item.quantity) === 1
                ? item.description
                : t("orders.itemLine", {
                    quantity: Number(item.quantity),
                    description: item.description,
                  })}
            </dt>
            <dd>
              <Amount
                money={{
                  minor: Math.round(item.unit_price.minor * Number(item.quantity)),
                  currency: item.unit_price.currency,
                }}
              />
            </dd>
          </div>
        ))}
        {order.notes && <p className="whitespace-pre-line text-stone-600">{order.notes}</p>}
      </dl>
    </div>
  );
}

function DetailsForm({ order, onDone }: { order: Order; onDone: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateOrder(order.id);
  const me = useQuery(meQuery);
  const currency = me.data?.workspace?.default_currency ?? "CAD";
  const form = useForm<OrderDetailsValues>({
    resolver: zodResolver(orderDetailsSchema),
    defaultValues: {
      description: order.description,
      due_date: order.due_date ?? "",
      quote: amountInput(order.quoted_total),
      notes: order.notes,
    },
  });
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync(toOrderPatch(values, currency));
      onDone();
    } catch (error) {
      form.setError("root", {
        message: error instanceof ApiError ? error.message : t("orders.errors.saveFailed"),
      });
    }
  });

  return (
    <form
      onSubmit={submit}
      noValidate
      className="space-y-3 rounded-lg border border-stone-200 bg-white p-4"
    >
      <Field
        name="edit-description"
        label={t("orders.new.description")}
        error={fieldMessage(errors, "description", t)}
      >
        <Input id="edit-description" {...form.register("description")} />
      </Field>
      <div className="flex gap-3">
        <div className="flex-1">
          <Field name="edit-due" label={t("orders.new.dueDate")}>
            <Input id="edit-due" type="date" {...form.register("due_date")} />
          </Field>
        </div>
        <div className="flex-1">
          <Field
            name="edit-quote"
            label={t("orders.quote")}
            error={fieldMessage(errors, "quote", t)}
          >
            <Input id="edit-quote" inputMode="decimal" {...form.register("quote")} />
          </Field>
        </div>
      </div>
      <Field name="edit-notes" label={t("orders.new.notes")}>
        <Input id="edit-notes" {...form.register("notes")} />
      </Field>
      {form.formState.errors.root?.message && (
        <Notice tone="error">{form.formState.errors.root.message}</Notice>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? t("common.saving") : t("common.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

// --- payments -----------------------------------------------------------------------------------

function Payments({ order }: { order: Order }) {
  const { t } = useTranslation();
  const payments = useQuery(orderPaymentsQuery(order.id));
  const money = useQuery(orderMoneyQuery(order.id));
  const remove = useDeletePayment(order.id);
  const [form, setForm] = useState<"payment" | "refund" | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();
  const open = takesPayments(order.status);
  // A cancelled order can still give a deposit back; a refunded one is done.
  const canGiveBack = order.status === "cancelled" && (money.data?.net_paid.minor ?? 0) > 0;

  async function confirmRemove(paymentId: string) {
    setError(undefined);
    try {
      await remove.mutateAsync(paymentId);
      setRemoving(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("orders.errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-2">
      <h2 className="font-medium text-stone-800">{t("orders.payments.title")}</h2>
      {payments.data?.length === 0 && (
        <p className="text-sm text-stone-500">{t("orders.payments.none")}</p>
      )}
      {payments.data && payments.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {payments.data.map((payment) => (
            <li key={payment.id} className="space-y-2 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1 text-sm">
                  <p className="text-stone-800">
                    {payment.direction === "in"
                      ? t("orders.payments.received", { method: payment.method.name })
                      : t("orders.payments.givenBack", { method: payment.method.name })}
                  </p>
                  <p className="text-stone-500">
                    {formatCalendarDate(payment.paid_on)}
                    {payment.note && ` · ${payment.note}`}
                  </p>
                </div>
                <Amount
                  money={
                    payment.direction === "in"
                      ? payment.amount
                      : { ...payment.amount, minor: -payment.amount.minor }
                  }
                  className={payment.direction === "in" ? "text-stone-800" : "text-red-700"}
                />
                {canRemovePayment(order.status, payment, payments.data) && (
                  <button
                    type="button"
                    className="p-1 text-stone-400 hover:text-red-700"
                    aria-label={t("orders.payments.remove")}
                    onClick={() => setRemoving(payment.id)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </div>
              {removing === payment.id && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex-1 text-stone-700">
                    {t("orders.payments.removeConfirm")}
                  </span>
                  <button
                    type="button"
                    className="font-medium text-red-700 underline"
                    disabled={remove.isPending}
                    onClick={() => void confirmRemove(payment.id)}
                  >
                    {t("orders.payments.remove")}
                  </button>
                  <button
                    type="button"
                    className="text-stone-500 underline"
                    onClick={() => setRemoving(null)}
                  >
                    {t("orders.keep")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <Notice tone="error">{error}</Notice>}

      {form ? (
        <PaymentForm
          order={order}
          mode={form}
          givingBack={!open}
          defaultAmount={form === "refund" ? (money.data?.net_paid ?? null) : null}
          onDone={() => setForm(null)}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {(open || canGiveBack) && (
            <Button
              type="button"
              variant="secondary"
              className="w-auto"
              onClick={() => setForm("payment")}
            >
              {open ? t("orders.payments.add") : t("orders.payments.giveBack")}
            </Button>
          )}
          {order.status === "completed" && (money.data?.net_paid.minor ?? 0) > 0 && (
            <Button
              type="button"
              variant="secondary"
              className="w-auto"
              onClick={() => setForm("refund")}
            >
              {t("orders.refund.start")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function PaymentForm({
  order,
  mode,
  givingBack,
  defaultAmount,
  onDone,
}: {
  order: Order;
  mode: "payment" | "refund";
  /** Only money going back is possible: a cancelled order's deposit. */
  givingBack: boolean;
  defaultAmount: OrderMoney["net_paid"] | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const me = useQuery(meQuery);
  const currency = me.data?.workspace?.default_currency ?? "CAD";
  const methods = useQuery(paymentMethodsQuery);
  const record = useRecordPayment(order.id);
  const refund = useRefund(order.id);
  const active = (methods.data ?? []).filter((method) => method.is_active);
  const form = useForm<PaymentValues>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      direction: givingBack || mode === "refund" ? "out" : "in",
      method_id: "",
      amount: defaultAmount ? amountInput(defaultAmount) : "",
      paid_on: todayIso(),
      note: "",
    },
  });
  const errors = form.formState.errors;
  const direction = useWatch({ control: form.control, name: "direction" });

  const submit = form.handleSubmit(async (values) => {
    try {
      if (mode === "refund") await refund.mutateAsync(toRefundBody(values, currency));
      else await record.mutateAsync(toPaymentBody(values, currency));
      onDone();
    } catch (error) {
      form.setError("root", {
        message: error instanceof ApiError ? error.message : t("orders.errors.saveFailed"),
      });
    }
  });

  return (
    <form
      onSubmit={submit}
      noValidate
      className="space-y-3 rounded-lg border border-stone-200 bg-white p-4"
    >
      {mode === "refund" ? (
        <p className="text-sm text-stone-700">{t("orders.refund.explain")}</p>
      ) : (
        !givingBack && (
          <div className="flex gap-2">
            {(["in", "out"] as const).map((value) => (
              <label key={value} className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  value={value}
                  checked={direction === value}
                  {...form.register("direction")}
                />
                {value === "in"
                  ? t("orders.payments.directionIn")
                  : t("orders.payments.directionOut")}
              </label>
            ))}
          </div>
        )
      )}
      <Field
        name="method_id"
        label={t("orders.payments.method")}
        error={fieldMessage(errors, "method_id", t)}
      >
        <Select id="method_id" {...form.register("method_id")}>
          <option value="">{t("orders.payments.chooseMethod")}</option>
          {active.map((method) => (
            <option key={method.id} value={method.id}>
              {method.name}
            </option>
          ))}
        </Select>
      </Field>
      <div className="flex gap-3">
        <div className="flex-1">
          <Field
            name="amount"
            label={t("orders.payments.amount", { currency })}
            error={fieldMessage(errors, "amount", t)}
          >
            <Input id="amount" inputMode="decimal" {...form.register("amount")} />
          </Field>
        </div>
        <div className="flex-1">
          <Field
            name="paid_on"
            label={t("orders.payments.date")}
            error={fieldMessage(errors, "paid_on", t)}
          >
            <Input id="paid_on" type="date" {...form.register("paid_on")} />
          </Field>
        </div>
      </div>
      <Field name="note" label={t("orders.payments.note")}>
        <Input id="note" {...form.register("note")} />
      </Field>
      {errors.root?.message && <Notice tone="error">{errors.root.message}</Notice>}
      <div className="flex gap-2">
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting
            ? t("common.saving")
            : mode === "refund"
              ? t("orders.refund.save")
              : t("orders.payments.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

// --- purchases ----------------------------------------------------------------------------------

function Purchases({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const purchases = useQuery(orderPurchasesQuery(orderId));
  return (
    <div className="space-y-2">
      <h2 className="font-medium text-stone-800">{t("orders.purchases.title")}</h2>
      {purchases.data?.length === 0 && (
        <p className="text-sm text-stone-500">{t("orders.purchases.none")}</p>
      )}
      {purchases.data && purchases.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {purchases.data.map((purchase) => (
            <li key={purchase.id}>
              <Link
                to="/receipts/$receiptId"
                params={{ receiptId: purchase.receipt_id }}
                className="flex items-start gap-3 px-4 py-3 text-sm hover:bg-stone-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-stone-800">{purchase.description}</p>
                  <p className="text-stone-500">
                    {[
                      purchase.store_name,
                      purchase.purchase_date && formatCalendarDate(purchase.purchase_date),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {!purchase.counted && (
                    <p className="text-amber-800">{t("orders.purchases.inReview")}</p>
                  )}
                </div>
                <Amount
                  money={purchase.amount}
                  className={purchase.counted ? "text-stone-800" : "text-stone-400"}
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
